/**
 * stepNodeStyles.ts - Comprehensive styles for automation step nodes
 *
 * Purpose:
 *   Detailed styling for all automation step nodes including expanded/collapsed states,
 *   badges, indicators, and interactive elements.
 *
 * Usage:
 *   import { stepNodeStyles } from './stepNodeStyles';
 *   <div className={css(stepNodeStyles.expandedCard)} />
 *
 * States:
 *   - compactCard: Collapsed node state
 *   - expandedCard: Expanded node state with full content
 *   - selected: Visual highlight for selected nodes
 *   - invalid: Error state with validation warnings
 *
 * Features:
 *   - Contact badges and indicators
 *   - Parent case indicators (for switch steps)
 *   - Read-only mode badges
 *   - Switch step branch visualizations
 *
 * Dependencies:
 *   - colors.ts for color palette
 *   - styles.ts for cardStyle base
 *
 * Related:
 *   - AutomationStepNodeWrapper.tsx
 *   - nodes/styles.ts (base node styles)
 *   - SwitchStepRenderer.tsx
 */

import { StyleSheet } from 'aphrodite';
import {
	brandPrimary,
	titles,
	charmGray,
	white,
	antiFlashWhite,
	backgroundAlt,
	destructive,
} from '../../../styles/colors';
import { cardStyle } from '../../../styles/styles';

/**
 * Color Mapping (from hardcoded → theme):
 * #F2F2F2 → antiFlashWhite
 * #F3F4F6 → backgroundAlt
 * #6B7280 → charmGray
 * #DC2626 → destructive
 * #FFFFFF → white
 */

export const stepNodeStyles = StyleSheet.create({
	nodeContainer: {
		position: 'relative',
		transition: 'all 0.3s ease',
	},

	expandedCard: {
		...cardStyle,
		width: 420,
		minHeight: 200,
		maxHeight: 'none', // Allow node to grow as needed
		overflowX: 'hidden',
		overflowY: 'visible', // No scrollbar by default
		boxSizing: 'border-box',
		scrollBehavior: 'smooth',
		display: 'flex',
		flexDirection: 'column',
	},

	expandedContent: {
		padding: '0 20px 20px',
		boxSizing: 'border-box',
		width: '100%',
		flex: 1,
	},

	expandedHeader: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		padding: '14px 20px',
		borderBottom: `1px solid ${antiFlashWhite}`,
	},

	headerLeft: {
		display: 'flex',
		alignItems: 'center',
		gap: 12,
		flex: 1,
	},

	headerTitle: {
		fontSize: 16,
		fontWeight: 600,
		color: titles,
	},

	collapseButton: {
		background: 'transparent',
		border: 'none',
		cursor: 'pointer',
		padding: 8,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		borderRadius: 4,
		transition: 'background 0.2s ease',
		minHeight: 44,
		minWidth: 44,
		':hover': {
			background: backgroundAlt,
		},
		':focus': {
			outline: `3px solid ${brandPrimary}40`,
			outlineOffset: 2,
		},
	},

	expandButton: {
		background: 'transparent',
		border: 'none',
		cursor: 'pointer',
		padding: 8,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		minHeight: 44,
		minWidth: 44,
		':focus': {
			outline: `3px solid ${brandPrimary}40`,
			outlineOffset: 2,
		},
	},

	compactCard: {
		...cardStyle,
		width: 420,
		minHeight: 100,
		maxHeight: 100,
		padding: 16,
		cursor: 'pointer',
		boxSizing: 'border-box',
		overflow: 'hidden',
		transition: 'all 0.2s ease',
		':hover': {
			boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
			borderColor: brandPrimary,
		},
	},

	compactHeader: {
		display: 'flex',
		alignItems: 'center',
		gap: 12,
	},

	compactTitle: {
		flex: 1,
		fontSize: 14,
		fontWeight: 600,
		color: titles,
	},

	compactSchedule: {
		marginTop: 8,
		fontSize: 12,
		color: charmGray,
	},

	selected: {
		boxShadow: '0 0 0 3px rgba(99, 102, 241, 0.3)',
	},

	invalid: {
		borderColor: destructive,
	},

	validationWarning: {
		marginTop: 8,
		display: 'flex',
		alignItems: 'center',
		gap: 6,
		fontSize: 12,
		color: destructive,
	},

	handle: {
		width: 12,
		height: 12,
		borderRadius: '50%',
		border: '2px solid white',
		background: brandPrimary,
	},

	parentCaseIndicator: {
		position: 'absolute',
		top: '-8px',
		left: '-8px',
		display: 'flex',
		alignItems: 'center',
		gap: 4,
		color: white,
		borderRadius: 12,
		padding: '2px 8px',
		fontSize: 10,
		fontWeight: 600,
		boxShadow: '0 2px 4px rgba(0, 0, 0, 0.15)',
		zIndex: 10,
	},

	contactBadgeContainer: {
		position: 'absolute',
		top: '-8px',
		right: '-8px',
		display: 'flex',
		gap: 2,
		backgroundColor: '#fff',
		borderRadius: 12,
		padding: '4px 6px',
		boxShadow: '0 2px 4px rgba(0, 0, 0, 0.15)',
		zIndex: 10,
	},

	contactColorDot: {
		width: 12,
		height: 12,
		borderRadius: '50%',
		boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
	},

	contactOverflow: {
		fontSize: 10,
		fontWeight: 600,
		color: charmGray,
		marginLeft: 2,
		lineHeight: '12px',
	},

	contactCountBadge: {
		fontSize: 10,
		fontWeight: 600,
		color: charmGray,
		marginLeft: 2,
	},

	readOnlyBadge: {
		backgroundColor: charmGray,
		color: white,
		padding: '2px 8px',
		borderRadius: 4,
		fontSize: 10,
		fontWeight: 600,
		textTransform: 'uppercase' as const,
		letterSpacing: '0.5px',
	},

	switchCompactInfo: {
		marginTop: 8,
		display: 'flex',
		flexDirection: 'column' as const,
		gap: 4,
	},

	switchBranchCount: {
		fontSize: 11,
		color: charmGray,
		fontWeight: 500,
	},

	switchCaseIndicators: {
		display: 'flex',
		flexDirection: 'row' as const,
		alignItems: 'center',
		gap: 4,
	},

	switchCaseDot: {
		width: 12,
		height: 12,
		borderRadius: '50%',
		flexShrink: 0,
	},

	switchOverflow: {
		fontSize: 10,
		color: charmGray,
		fontWeight: 600,
		marginLeft: 2,
	},
});
